import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import { toast } from "react-toastify";

// UI
// import { BiExport } from "react-icons/bi"; // (currently unused)
// import { BsPencil } from "react-icons/bs"; // left for future inline edit icons
// import { PiCheckCircleBold } from "react-icons/pi";

// App modules
import { loaderReducer } from "../../../components/toolkit/loader";
// import userCheck from "../../../utils/checkUser";
import { UpdateTask, updateAnswer } from "../../../functions/task";
import { useCan } from "../../../utils/permissions";

// Child components
import TaskInfoSection from "../../workOrder/tasks/content/TaskInfoSection";
import SpecialInstructionsEditor from "../../workOrder/tasks/content/SpecialInstructionsEditor";
import AnswerList from "../../workOrder/tasks/content/AnswerList";

/**
 * Props
 * - setIndividualTask: function (from parent, not used here but kept for API compatibility)
 * - arg: {
 *     handleClickTech, isVisibleTech,
 *     activeEdit, handleActiveEdit, setActiveEdit,
 *     individualTask,
 *     setUpdateData, updateData,
 *     handleActionWithoutSaving,
 *     customFields, setCustomFields,
 *     ndata,
 *   }
 */
const TaskDetailsPanel = ({ setIndividualTask, arg }) => {
  const {
    // handleClickTech, // not used in this component
    // isVisibleTech,   // not used in this component
    activeEdit,
    handleActiveEdit,
    setActiveEdit,
    individualTask,
    setUpdateData,
    handleActionWithoutSaving, // still wired for "exit without saving" if you want to restore the button
    customFields,
    setCustomFields,
  } = arg;

  const cookies = new Cookies();
  const token = cookies.get("token");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const bottomRef = useRef(null);
  const can = useCan();
  const canUpdateAnswer = can("answer", "update");
  const canUpdateTask = can("task", "update");
  const [canEditTaskSurvey, setCanEditTaskSurvey] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Local form state mirrors `individualTask` fields we are allowed to edit
  const [formData, setFormData] = useState({
    id: "",
    tranId: "",
    device: "",
    room: "",
    description: "",
    productInstall: "",
    serialNumber: "",
    customImage: [],
    question: [],
    techName: "",
    taskNote: "",
    custcolVersion: "",
  });

  // Answers payload state for updateAnswer API
  const [answerPayload, setAnswerPayload] = useState({
    surveyId: "",
    userId: "",
    answer: [],
  });

  // --- Effects --------------------------------------------------------------
  useEffect(() => {
    setCanEditTaskSurvey(canUpdateAnswer);
  }, []);

  // Sync `individualTask` → form state
  useEffect(() => {
    if (!individualTask) return;
    setFormData((prev) => ({
      ...prev,
      id: individualTask._id || prev.id,
      tranId: individualTask.tranId || "",
      device: individualTask.device || "",
      room: individualTask.room || "",
      description: individualTask.description || "",
      productInstall: individualTask.productInstall || false,
      serialNumber: individualTask.serialNumber || "",
      techName: individualTask?.techName || "",
      customImage: individualTask?.customImage || [],
      question: Array.isArray(prev.question) ? prev.question : [],
      taskNote: individualTask?.taskNote || "",
      custcolVersion: individualTask?.custcolVersion || "",
    }));
  }, [individualTask]);

  // Sync `customFields` → answer payload
  useEffect(() => {
    if (!customFields) return;
    setAnswerPayload({
      surveyId: customFields?.surveyId?._id || "",
      userId: customFields?.userId || "",
      answer: customFields?.answer || [],
    });
  }, [customFields]);

  // --- Handlers -------------------------------------------------------------
  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Accept either a synthetic event (e.target.name/value) or plain string for description
  const handleChange = useCallback((eOrValue) => {
    let name;
    let value;

    if (eOrValue && typeof eOrValue === "object" && eOrValue.target) {
      ({ name, value } = eOrValue.target);
    } else {
      // SpecialInstructionsEditor sends raw string
      name = "description";
      value = eOrValue ?? " ";
    }

    // Rename adapter for TaskInfoSection (versionAssigned → custcolVersion)
    if (name === "versionAssigned") name = "custcolVersion";

    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleImageRemove = useCallback((answer) => {
    if (!customFields?.answer) return;
    const idx = customFields.answer.findIndex((a) => a.uuid === answer.uuid);
    if (idx === -1) return;

    const updated = [...customFields.answer];
    updated[idx] = { ...updated[idx], image: "", geotag: "false" };
    setCustomFields((prev) => ({ ...prev, answer: updated }));
  }, [customFields, setCustomFields]);

  const handleAnswerChange = useCallback((_question, _newValue) => {
    // Hook for per-answer changes if AnswerList supports it later
  }, []);

  const goToWorkOrder = useCallback(() => {
    const tranId = formData?.tranId || individualTask?.tranId;
    if (tranId) {
      navigate("/work-order", { state: { tranId } });
    } else {
      toast.error("Try again!");
    }
  }, [formData?.tranId, individualTask?.tranId, navigate]);

  const handleSave = useCallback(async () => {
    if (!individualTask?.tranId) return toast.error("Missing tranId");

    dispatch(loaderReducer(true));
    setIsSaving(true);

    try {
      // 1) Update survey answers (if present)
      const payload = {
        surveyId: answerPayload.surveyId || customFields?.surveyId?._id,
        userId: answerPayload.userId || customFields?.userId,
        taskUniqueId: individualTask?.taskUniqueId,
        taskId: individualTask?._id,
        tranId: individualTask?.tranId,
        serialNumber: formData?.serialNumber,
        custcolVersion: formData?.custcolVersion,
        specialInstructions: formData?.description,
        answer: customFields?.answer.map((item) => ({
          ...item,
          answer: item.answer?.toString()?.trim() || null,
          image: item.image || (item.questionType === "photo" ? null : undefined),
          geotag: item.geotag || "false",
        })),
      };


      if (payload.userId && individualTask._id) {
        await updateAnswer(individualTask?._id, payload);
      }

      // 2) Update task details
      const res = await UpdateTask(individualTask.tranId, token, formData);
      if (typeof setUpdateData === "function") setUpdateData(res.data);

      setActiveEdit?.(false);
      toast.success("Saved");
    } catch (err) {
      console.error("handleSave error", err);
      toast.error("Failed to save changes");
    } finally {
      dispatch(loaderReducer(false));
      setIsSaving(false);
    }
  }, [answerPayload, customFields, dispatch, formData, individualTask?.tranId, setActiveEdit, setUpdateData, token]);

  // --- Render ----------------------------------------------------------------
  const showEditButton = useMemo(() => Boolean(individualTask && canEditTaskSurvey), [individualTask, canEditTaskSurvey]);

  return (
    <div>
      {showEditButton && (
        <div
          className="editbtn"
          onClick={() => {
            handleActiveEdit?.();
            scrollToBottom();
          }}
          style={activeEdit ? { color: "#1d5de6" } : undefined}
        >
          {activeEdit ? "Cancel Edit" : "Edit Info"}
        </div>
      )}

      <div className="tupdate mt-4">Details</div>

      <TaskInfoSection
        tranId={formData?.tranId || individualTask?.tranId}
        techName={formData?.techName}
        serialNumber={formData?.serialNumber}
        versionAssigned={formData.custcolVersion}
        onSerialNumberChange={handleChange}
        onVersionChange={handleChange}
        canEdit={activeEdit}
      />

      {/* Room is read-only in view mode */}
      {!activeEdit && (
        <div className="row mt-3">
          <div className="col-lg-2 col-md-4 col-sm-12 sub2head">Room #</div>
          <div className="col-lg-10 col-md-8 col-sm-12 position-relative">
            <input className="form-control" type="text" name="room" value={formData.room} disabled />
          </div>
        </div>
      )}

      <SpecialInstructionsEditor
        value={
          activeEdit
            ? (formData?.description ?? "")
            : (formData?.description && formData.description.trim().length > 0
              ? formData.description
              : "None")
        }
        onChange={handleChange}
        canEdit={activeEdit}
      />
      <AnswerList
        answers={customFields?.answer}
        setCustomFields={setCustomFields}
        customFields={customFields}
        onImageRemove={handleImageRemove}
        onAnswerChange={handleAnswerChange}
        canEdit={activeEdit}
      />

      <div ref={!customFields?.answer ? bottomRef : null}>
        <p>
          <label htmlFor="taskNote" className="tupdate mt-4">
            Technician Notes
          </label>
        </p>
        <textarea
          className="form-control"
          id="taskNote"
          name="taskNote"
          value={formData?.taskNote}
          onChange={handleChange}
          placeholder="No notes found!"
          disabled={!activeEdit}
        />
      </div>

      {/* Actions */}
      <div className="d-flex mt-4 gap-2 justify-content-end">
        {/* Restore this if you want an explicit exit without saving button */}
        <div className="contd" onClick={activeEdit ? handleActionWithoutSaving : goToWorkOrder}>
          {activeEdit ? "Exit without Saving" : (<></>)}
        </div>

        <div
          className="contd"
          style={{ backgroundColor: "#1C449C", color: "white", opacity: isSaving ? 0.7 : 1, pointerEvents: isSaving ? "none" : "auto" }}
          onClick={activeEdit ? handleSave : goToWorkOrder}
        >
          {activeEdit ? (isSaving ? "Saving..." : "Save Changes") : "Go to Work Order"}
        </div>
      </div>
    </div>
  );
};

export default TaskDetailsPanel;

